import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./history-log.css";
import AppDatePickerRange from "../orders/components/AppDatePickerRange";
import { fetchHistoryLogsAsync } from "src/redux/actions/historyLogsActions";
import Loader from 'src/utils/Loader';
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "src/redux/store";
import {
  Box, Stepper, Step, StepLabel, StepContent, Card, CardContent,
  Typography, Chip, Stack, Divider, StepConnector, stepConnectorClasses
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faTrash, faCalendar } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { useAppSelector } from "src/redux/storeHooks";
import { useSearchParams } from "react-router-dom";
import { getAllPlatformTypesAsync } from "src/redux/actions/platformActions";
import Tooltip from '@mui/material/Tooltip';
import { formatTimeZone } from "src/utils/TimeZoneDateFormat";
import { Spinner } from "react-bootstrap";
import { clearHistoryLogsData, resetHistoryLogs } from "src/redux/slices/historyLogsSlice";

interface DiffSummary {
  OLD: string;
  NEW: string;
  Message: boolean;
}

interface HistoryItem {
  id: number;
  action_type: string;
  content_type_name: string;
  changed_by_email: string | null;
  platform_name: string | null;
  changed_at: string;
  short_title: string;
  diff_summary?: DiffSummary;
  show_old_new_view: boolean;
}

const HistoryLogs = () => {
  const { fetchedUserAccountData } = useAppSelector(state => state.userAccountData)
  const { historyLogsLoading, historyLogsData, historyLogsError, page, hasMore, } = useSelector((state: any) => state.historyLogs);
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
  const [selectedRange, setSelectedRange] = useState<any>(null);
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  let platform_id = searchParams.get('platform_id');
  const { fetchedConnectedPlatformData } = useAppSelector((state) => state.fetchConnectedPlatform);
  const [selectedPlatform, setSelectedPlatform] = useState(platform_id ?? '');
  const [groupedData, setGroupedData] = useState<{ date: string; items: HistoryItem[] }[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const isInitialLoading = historyLogsLoading && page === 1;
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [cardExpanded, setCardExpanded] = useState<{ [key: number]: boolean }>({});
  const [isFreshLoad, setIsFreshLoad] = useState(false);

  useEffect(() => {
    setIsFreshLoad(true);
    dispatch(resetHistoryLogs());
    dispatch(fetchHistoryLogsAsync({
      page: 1, user_id: Number(fetchedUserAccountData?.id),
      platform_id: selectedPlatform ? Number(selectedPlatform) : undefined,
      changed_at_min: selectedRange?.changed_at_min, changed_at_max: selectedRange?.changed_at_max
    }));

  }, [selectedPlatform, selectedRange,]);

  useEffect(() => {
    const fetchConnectedPlatforms = async () => {
      try {
        const response = await dispatch(getAllPlatformTypesAsync()).unwrap();
        const data = response?.metadata?.logs?.order_logs?.fields
      } catch (error) {
        console.error('Failed to fetch platform types:', error);
      }
    }
    fetchConnectedPlatforms();
  }, [])

  useEffect(() => {
    if (!historyLogsData || historyLogsData.length === 0) {
      setGroupedData([]);
      return;
    }

    // Helper function to convert ISO date to DD-MM-YYYY
    const formatDate = (isoDate: string) =>
      isoDate.slice(0, 10).split("-").reverse().join("-");

    if (isFreshLoad) {
      const filteredData = historyLogsData.filter(
        (entry) => entry.diff_summary !== null && entry.diff_summary !== undefined
      );

      // If everything is filtered out
      if (filteredData.length === 0) {
        setGroupedData([]);
        return;
      }
      // Type-safe grouping by date
      const groupedObj: Record<string, HistoryItem[]> = historyLogsData.reduce(
        (acc, entry) => {
          const date = formatDate(entry.changed_at);
          if (!acc[date]) acc[date] = [];
          acc[date].push(entry);
          return acc;
        },
        {} as Record<string, HistoryItem[]>
      );

      const newGrouped = Object.values(groupedObj).map((items) => ({
        date: formatDate(items[0].changed_at),
        items: items.sort(
          (a, b) =>
            new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime()
        ),
      }));
      const filteredGrouped = newGrouped.map(group => ({
        ...group,
        items: group.items.filter(item => item.diff_summary)
      }));
      console.log("Fresh Load - New Grouped Data:", filteredGrouped);

      setGroupedData(filteredGrouped);
      setIsFreshLoad(false); // reset the flag
    } else {
      // Pagination merge logic
      setGroupedData((prev) => {
        const map = new Map(prev.map((g) => [g.date, g.items]));

        for (const entry of historyLogsData) {
          const date = formatDate(entry.changed_at);
          if (!map.has(date)) map.set(date, []);
          map.get(date)!.push(entry);
        }

        const merged = Array.from(map.entries()).map(([date, items]) => ({
          date,
          items: items.sort(
            (a, b) =>
              new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime()
          ),
        }));

        return merged.sort(
          (a, b) =>
            new Date(b.date.split("-").reverse().join("-")).getTime() -
            new Date(a.date.split("-").reverse().join("-")).getTime()
        );
      });
    }
  }, [historyLogsData, selectedPlatform, selectedRange]);


  const CustomConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
      top: 10,
    },
    [`&.${stepConnectorClasses.active}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.divider,
      },
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.divider,
      },
    },
    [`& .${stepConnectorClasses.line}`]: {
      minHeight: 30,
      marginLeft: 0,
      borderLeftWidth: 1,
      borderColor: theme.palette.divider,
    },
  }));

  const handleResetFilters = () => {
    setSelectedPlatform('');
    setSelectedRange(null);
  }
  const isFetchingRef = useRef(false);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const scrollTop = target.scrollTop;
      const clientHeight = target.clientHeight;
      const scrollHeight = target.scrollHeight;

      const threshold = 250;
      setShowScrollTop(scrollTop > scrollHeight / 8);

      if (
        scrollHeight - scrollTop - clientHeight <= threshold &&
        hasMore &&
        !historyLogsLoading &&
        !isFetchingRef.current
      ) {
        isFetchingRef.current = true;

        dispatch(
          fetchHistoryLogsAsync({
            page, user_id: Number(fetchedUserAccountData?.id),
            platform_id: selectedPlatform ? Number(selectedPlatform) : undefined,
            changed_at_min: selectedRange?.changed_at_min,
            changed_at_max: selectedRange?.changed_at_max,
          })
        ).finally(() => {
          isFetchingRef.current = false;
        });
      }
    },
    [hasMore, historyLogsLoading, selectedPlatform, selectedRange, page, dispatch]
  );

  const toggleCardExpand = (id: number) => {
    setCardExpanded(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const Chevron = ({ open }: { open: boolean }) => (
    <span
      style={{
        display: "inline-block",
        transition: "0.2s",
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
      }}
    >
      ▼
    </span>
  );
  const clampStyle = {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    WebkitLineClamp: 2, // number of visible lines
    lineHeight: "1.3rem"
  };

  return (
    <>
      {/* <div className='mb-3'>
        <span className='fw-bold text-lg ' style={{ fontSize: '16px' }}>
          History Logs
        </span>
      </div> */}
      <div
        className="history-container"
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        <div className="timeline-card wider-card">
          { }
          <div className="filter-section">
            <select
              className='form-select filter-dropdown'
              aria-label='Select platform'
              value={selectedPlatform}
              onChange={(ev) => {
                setSelectedPlatform(ev.target.value)
              }}
            >
              <option value={''}>All Channels</option>
              {fetchedConnectedPlatformData.length &&
                fetchedConnectedPlatformData?.map((item) => {
                  return (
                    <option key={item?.id + 'platform'} value={item?.id}>
                      {item?.display_name}
                    </option>
                  )
                })}
            </select>

            <button
              className="btn btn-outline-primary btn-sm date-btn"
              onClick={() => setIsDatePickerVisible(true)}
            >
              <FontAwesomeIcon icon={faCalendar} className="me-2" />
              {selectedRange
                ? `${selectedRange.startDate.split("T")[0]} → ${selectedRange.endDate.split("T")[0]}`
                : "Pick Date Range"}
            </button>
            <button
              className='btn btn-sm btn-primary w-100px fw-bold py-2'
              onClick={handleResetFilters}
            >
              Reset Filters
            </button>

          </div>

          {isInitialLoading ? (
            <div className='text-center py-10'>
              <Loader variant="section" />
            </div>
          ) : (
            // Timeline
            <Box sx={{ maxWidth: 900, mx: "auto", mt: 4 }}>
              <Stepper
                orientation="vertical"
                connector={<CustomConnector />}
                sx={{
                  "& .MuiStepIcon-root": { display: "none" },
                  "& .MuiStepConnector-line": {
                    minHeight: 30,
                  },
                  "& .MuiStepContent-root": {
                    borderLeft: "1px solid #bdbdbd !important",
                    marginLeft: "30px",
                    paddingLeft: "20px",
                    paddingRight: "8px",
                  },
                  "& .MuiStepConnector-root .MuiStepConnector-line": {
                    borderColor: "#bdbdbd !important",
                    position: "relative",
                    left: "18px",
                    marginBottom: "-1rem !important",
                  },
                }}
              >
                {groupedData.length > 0 ? groupedData.map((day, dayIndex) => (
                  <Step key={`${day.date}-${dayIndex}`} expanded>
                    {/* Date Label */}
                    <StepLabel
                      icon={null}
                      sx={{
                        "& .MuiStepLabel-label": {
                          fontWeight: 700,
                          color: "#fff",
                          backgroundColor: "#d32f2f",
                          px: 2,
                          py: 0.5,
                          borderRadius: "6px",
                          fontSize: "0.9rem",
                          boxShadow: "0 3px 6px rgba(211,47,47,0.35)",
                          textShadow: "0 1px 2px rgba(0,0,0,0.25)",
                          width: "7.5rem",
                          position: "relative",
                          left: "-1rem",
                          marginBottom: "-1rem",
                        },
                        "& .MuiStepLabel-label.Mui-active": { color: "#fff" },
                        "& .MuiStepLabel-label.Mui-completed": { color: "#fff" },
                      }}
                    >
                      {day.date}
                    </StepLabel>


                    {/* Step Content */}
                    <StepContent>
                      <Stack spacing={2} mt={2}>
                        {day.items.map((entry, idx) => {
                          const isLast =
                            dayIndex === groupedData.length - 1 &&
                            idx === day.items.length - 1;
                          const showMessage = entry?.show_old_new_view;
                          return (
                            <Card
                              key={entry.id}
                              variant="outlined"
                              sx={{
                                maxHeight: cardExpanded[entry.id] ? "none" : "13rem",
                                overflow: "hidden",
                                borderRadius: 3,
                                borderLeft:
                                  entry.action_type === "insert"
                                    ? "6px solid #2e7d32" // green
                                    : entry.action_type === "delete"
                                      ? "6px solid #d32f2f" // red
                                      : "6px solid #0288d1", // blue
                                boxShadow:
                                  "0 4px 10px rgba(0,0,0,0.08), 0 2px 5px rgba(0,0,0,0.04)",
                                background:
                                  entry.action_type === "delete"
                                    ? "linear-gradient(135deg, #fff5f5 0%, #ffeaea 100%)"
                                    : entry.action_type === "insert"
                                      ? "linear-gradient(135deg, #f4fff5 0%, #e8f9ec 100%)"
                                      : "linear-gradient(135deg, #f4faff 0%, #e8f3ff 100%)",
                                transition: "transform 0.25s ease, box-shadow 0.25s ease, max-height 0.3s ease",
                                "&:hover": {
                                  transform: "translateY(-3px)",
                                  boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                                },
                              }}
                            >

                              <CardContent sx={{ p: "10px", pb: "10px !important" }}>
                                { }
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <FontAwesomeIcon
                                      icon={
                                        entry.action_type === "insert"
                                          ? faPlus
                                          : entry.action_type === "delete"
                                            ? faTrash
                                            : faEdit
                                      }
                                      color={
                                        entry.action_type === "insert"
                                          ? "#2e7d32"
                                          : entry.action_type === "delete"
                                            ? "#d32f2f"
                                            : "#0288d1"
                                      }
                                    />
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: 700,
                                        letterSpacing: 0.2,
                                        fontSize: "1rem",
                                        color:
                                          entry.action_type === "insert"
                                            ? "#2e7d32"
                                            : entry.action_type === "delete"
                                              ? "#d32f2f"
                                              : "#0288d1",
                                        textShadow: "0 1px 2px rgba(0,0,0,0.08)",
                                      }}
                                    >
                                      {entry.short_title ||
                                        (entry.action_type === "insert"
                                          ? "Mapping Added"
                                          : entry.action_type === "delete"
                                            ? "Mapping Deleted"
                                            : "Mapping Updated")}
                                    </Typography>

                                  </Stack>
                                  <Tooltip title={formatTimeZone()} placement="top">
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}
                                    >
                                      <i className="bi bi-clock"></i>
                                      {moment(entry.changed_at).format("HH:mm:ss")}
                                    </Typography>
                                  </Tooltip>
                                </Stack>

                                {/* Meta Info */}
                                <Stack direction="row" spacing={8} >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                  >
                                    <span style={{
                                      color: "#3d98c3",
                                      fontWeight: "bold"
                                    }}>
                                      Changed By{" "}
                                    </span>
                                    <strong style={{ color: "black" }}>
                                      {entry.changed_by_email || "System"}
                                    </strong>{" "}
                                    <span style={{
                                      color: "#3d98c3",
                                      fontWeight: "bold"
                                    }}>on Channel{" "} </span>
                                    <strong style={{ color: "black" }}>
                                      {entry.platform_name || "N/A"}
                                    </strong>
                                  </Typography>

                                  {/* <Divider sx={{ my: 1.5 }} /> */}

                                  {/* Model Field */}
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Model:{" "}
                                    <span style={{ fontWeight: 400 }}>
                                      {entry?.content_type_name}
                                    </span>
                                  </Typography>
                                </Stack>

                                {/* Enhanced Diff Summary */}
                                {entry?.diff_summary && (
                                  (showMessage === false && entry.action_type === "delete") ? (
                                    // --- SHOW SUMMARY WHEN message is off + delete ---
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Summary
                                      </Typography>

                                      <Box
                                        sx={{
                                          backgroundColor: "#ffffff",
                                          p: 2,
                                          borderRadius: 1.5,
                                          minHeight: 40,
                                          border: "1px solid #e0e0e0",
                                        }}
                                      >
                                        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "pre-wrap" }}>
                                          {entry.diff_summary?.Message || "-"}
                                        </Typography>
                                      </Box>
                                    </Box>

                                  ) : showMessage === false ? (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Summary
                                      </Typography>

                                      <Box
                                        sx={{
                                          backgroundColor: "#ffffff",
                                          p: 2,
                                          borderRadius: 1.5,
                                          minHeight: 40,
                                          border: "1px solid #e0e0e0",
                                        }}
                                      >
                                        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "pre-wrap" }}>
                                          {entry.diff_summary?.Message || "-"}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Box sx={{ mt: 1 }}>
                                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                          Changed Values:
                                        </Typography>

                                        {/* SEE MORE / SEE LESS */}
                                        {(entry.diff_summary?.OLD?.length > 180 || entry.diff_summary?.NEW?.length > 180) && (
                                          <button
                                            onClick={() => toggleCardExpand(entry.id)}
                                            style={{
                                              border: "1px solid #0288d1",
                                              background: "#fff",
                                              borderRadius: "6px",
                                              padding: "2px 8px",
                                              cursor: "pointer",
                                              color: "#0288d1",
                                              fontWeight: 600,
                                              fontSize: "0.75rem",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "6px",
                                              transition: "0.2s"
                                            }}
                                          >
                                            {cardExpanded[entry.id] ? "See Less" : "See More"}
                                            <Chevron open={cardExpanded[entry.id]} />
                                          </button>
                                        )}
                                      </Stack>

                                      <Stack
                                        direction="row"
                                        spacing={0}
                                        divider={<Divider orientation="vertical" flexItem />}
                                      >
                                        {/* OLD side */}
                                        {(entry.action_type !== "insert" && entry.action_type !== "delete") && (
                                          <Box sx={{ flex: 1, pr: 2 }}>
                                            <Chip
                                              label="OLD"
                                              size="small"
                                              sx={{
                                                backgroundColor: "#757575",
                                                color: "#fff",
                                                fontWeight: 700,
                                                letterSpacing: 0.5,
                                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                                mb: "4px",
                                              }}
                                            />
                                            <Box
                                              sx={{
                                                backgroundColor: "#f9f9f9",
                                                p: "10px",
                                                borderRadius: 1.5,
                                                minHeight: 40,
                                                border: "1px solid #e0e0e0",
                                                boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
                                                maxHeight: cardExpanded[entry.id] ? "none" : "4.5rem",
                                                overflow: "hidden",
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontWeight: 600,
                                                  whiteSpace: "pre-wrap",
                                                  marginTop: "-5px",
                                                  ...(cardExpanded[entry.id] ? {} : clampStyle),
                                                }}
                                              >
                                                {entry.diff_summary.OLD || "-"}
                                              </Typography>
                                            </Box>
                                          </Box>
                                        )}

                                        {/* NEW side */}
                                        <Box sx={{ flex: 1, pl: 2 }}>
                                          <Chip
                                            label="NEW"
                                            size="small"
                                            sx={{
                                              backgroundColor: "#4caf50",
                                              color: "#fff",
                                              fontWeight: 700,
                                              letterSpacing: 0.5,
                                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                              mb: "4px",
                                            }}
                                          />
                                          <Box
                                            sx={{
                                              backgroundColor: "#f0fff4",
                                              p: "10px",
                                              borderRadius: 1.5,
                                              minHeight: 40,
                                              border: "1px solid #dcedc8",
                                              boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
                                              maxHeight: cardExpanded[entry.id] ? "none" : "4.5rem",
                                              overflow: "hidden",
                                            }}
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontWeight: 600,
                                                whiteSpace: "pre-wrap",
                                                marginTop: "-5px",
                                                ...(cardExpanded[entry.id] ? {} : clampStyle),
                                              }}
                                            >
                                              {entry.diff_summary.NEW || "-"}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </Stack>
                                    </Box>
                                  )
                                )}

                              </CardContent>
                            </Card>
                          )
                        }
                        )}
                      </Stack>
                    </StepContent>
                  </Step>
                ))
                  : (
                    <div className='p-4 bg-light-primary'>
                      <h5>No Available Logs found</h5>
                    </div>
                  )
                }
              </Stepper>
              {!hasMore && (
                <div className="text-center text-muted my-4">No more logs</div>
              )}
            </Box>
          )}
        </div>
        {historyLogsError && (
          <div className="alert alert-danger my-3">
            {typeof historyLogsError === "string"
              ? historyLogsError
              : historyLogsError?.message || historyLogsError?.response?.data?.message || "Something went wrong"}
          </div>
        )}

        {/* Date Picker */}
        <AppDatePickerRange
          isVisible={isDatePickerVisible}
          handleClose={() => setIsDatePickerVisible(false)}
          onPickRange={({ startDate, endDate }) =>
            setSelectedRange((pre: any) => ({
              ...pre,
              changed_at_min: moment(startDate).format('YYYY-MM-DD'),
              changed_at_max: moment(endDate).format('YYYY-MM-DD'),
              page: 1,
              startDate,
              endDate,
            }))
          }
        />
        {showScrollTop && (
          <button
            className="scroll-top-btn"
            onClick={() => {
              scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            ↑
          </button>
        )}

      </div>
    </>
  );
};

export default HistoryLogs;